import {
  COUNCILS,
  isAggregateCouncilScope,
  type CouncilSlug,
} from '../councils';

export type PublicPlanningCounts = Partial<Record<CouncilSlug, number>>;

type CountsPayload = {
  counts?: Partial<Record<string, unknown>>;
  fetched_at?: string;
  source?: string;
};

type PlanningFeedCountsResult = {
  counts: PublicPlanningCounts;
  fetchedAt: string | null;
  source: string | null;
};

type FetchPlanningFeedCountsOptions = {
  areaId?: string | null;
  fetchImpl?: typeof fetch;
};

const COUNTS_CACHE_TTL_MS = 5 * 60 * 1000;
const publicCouncilSlugs = Object.values(COUNCILS)
  .filter((council) => council.publicEnabled)
  .map((council) => council.slug);
type SharedCountsCache = {
  responseCache: Map<string, { result: PlanningFeedCountsResult; fetchedAt: number }>;
  inFlight: Map<string, Promise<PlanningFeedCountsResult>>;
};
const runtime = globalThis as typeof globalThis & {
  __cityScopePlanningFeedCountsCache?: SharedCountsCache;
};
// PlanningMapGL is dynamically loaded while the region rail lives in the main
// page chunk. Keeping the tiny cache on globalThis prevents webpack chunk
// duplication from turning those consumers into duplicate network requests.
const sharedCache = runtime.__cityScopePlanningFeedCountsCache || {
  responseCache: new Map(),
  inFlight: new Map(),
};
runtime.__cityScopePlanningFeedCountsCache = sharedCache;
const { responseCache, inFlight } = sharedCache;

const toCount = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : null;
};

export const normalizePublicPlanningCounts = (payload: CountsPayload | null | undefined) => {
  const sourceCounts = payload?.counts;
  const counts: PublicPlanningCounts = {};
  if (!sourceCounts || typeof sourceCounts !== 'object') return counts;

  publicCouncilSlugs.forEach((slug) => {
    const count = toCount(sourceCounts[slug]);
    if (count !== null) counts[slug] = count;
  });
  return counts;
};

export const getPlanningCountForScope = (
  counts: PublicPlanningCounts,
  councilSlug?: string | null,
) => {
  if (isAggregateCouncilScope(councilSlug)) {
    const availableCounts = publicCouncilSlugs
      .map((slug) => counts[slug])
      .filter((value): value is number => Number.isFinite(value));
    return availableCounts.length > 0
      ? availableCounts.reduce((total, value) => total + value, 0)
      : null;
  }

  const council = Object.values(COUNCILS).find((entry) => entry.slug === councilSlug);
  if (!council?.publicEnabled) return null;
  const count = counts[council.slug];
  return Number.isFinite(count) ? Number(count) : null;
};

const getCountsUrl = (areaId?: string | null) => {
  const params = new URLSearchParams();
  if (areaId) params.set('area_id', areaId);
  return `/api/planning-feed/counts${params.size > 0 ? `?${params}` : ''}`;
};

export async function fetchPlanningFeedCounts({
  areaId,
  fetchImpl = fetch,
}: FetchPlanningFeedCountsOptions = {}): Promise<PlanningFeedCountsResult> {
  const url = getCountsUrl(areaId);
  const now = Date.now();
  const cached = responseCache.get(url);
  if (cached && now - cached.fetchedAt < COUNTS_CACHE_TTL_MS) {
    return cached.result;
  }

  const activeRequest = inFlight.get(url);
  if (activeRequest) return activeRequest;

  const request = (async () => {
    const response = await fetchImpl(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Planning feed counts returned ${response.status}`);
    }

    const payload = await response.json() as CountsPayload;
    const result = {
      counts: normalizePublicPlanningCounts(payload),
      fetchedAt: typeof payload?.fetched_at === 'string' ? payload.fetched_at : null,
      source: typeof payload?.source === 'string' ? payload.source : null,
    };
    responseCache.set(url, { result, fetchedAt: Date.now() });
    return result;
  })().finally(() => {
    inFlight.delete(url);
  });

  inFlight.set(url, request);
  return request;
}

export const resetPlanningFeedCountsCacheForTests = () => {
  responseCache.clear();
  inFlight.clear();
};
